import React from 'react';
import { Button, InputNumber, message, Spin } from 'antd';
import StateSelect from 'components/form/StateSelect';
import { faxNumberSearch, faxNumberProvision } from 'api/fax';
import { isFaxScheduleSuccess } from 'utils/eFaxScheduleRows';
import type { FaxAccountVo } from 'types/eFax';
import { EFAX_PROVISION_SESSION_KEY } from 'types/eFax';
import s from './s.module.less';
import ps from '../s.module.less';

type NumberItem = {
    number: string;
    preferable?: boolean;
    regionalData?: {
        rateCenter?: string;
        state?: string;
        countryIso?: string;
    };
};

type Props = {
    onBack: () => void;
    onConfirm: () => void;
};

function formatPhoneNumber(num: string): string {
    const digits = num.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        const area = digits.slice(1, 4);
        const mid = digits.slice(4, 7);
        const last = digits.slice(7);
        return `+1 (${area}) ${mid} - ${last}`;
    }
    if (digits.length === 10) {
        const area = digits.slice(0, 3);
        const mid = digits.slice(3, 6);
        const last = digits.slice(6);
        return `+1 (${area}) ${mid} - ${last}`;
    }
    return num;
}

const NewNumberFlow = ({ onBack, onConfirm }: Props) => {
    const [state, setState] = React.useState<string | undefined>(undefined);
    const [areaCode, setAreaCode] = React.useState('');
    const [numbers, setNumbers] = React.useState<NumberItem[]>([]);
    const [selectedNumber, setSelectedNumber] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [searched, setSearched] = React.useState(false);

    const finishProvision = (account?: FaxAccountVo | null) => {
        if (account?.faxNumber) {
            try {
                sessionStorage.setItem(EFAX_PROVISION_SESSION_KEY, JSON.stringify(account));
            } catch {
                /* ignore quota */
            }
        }
        onConfirm();
    };

    const handleSearch = async () => {
        if (!state) {
            message.warning('Please select a state');
            return;
        }
        setLoading(true);
        setNumbers([]);
        setSelectedNumber(null);
        setSearched(false);
        try {
            const result = await faxNumberSearch({
                state,
                ...(areaCode && String(areaCode).length >= 3 ? { areaCode: String(areaCode) } : {}),
            });
            const respData = result?.data;
            if (result?.error) {
                message.error(result.error || 'Search failed');
            } else if (isFaxScheduleSuccess(respData)) {
                // Backend may return { code: 200, data: null } when no numbers — treat as empty list, not error
                const list = (respData as { data?: NumberItem[] | null }).data;
                setNumbers(Array.isArray(list) ? list : []);
            } else {
                message.error((respData as { message?: string })?.message || 'Search failed');
            }
        } catch {
            message.error('Search failed. Please try again.');
        } finally {
            setLoading(false);
            setSearched(true);
        }
    };

    const handleConfirm = async () => {
        if (!selectedNumber) return;
        setLoading(true);
        const result = await faxNumberProvision({ number: selectedNumber, type: 'order' });
        const body = result?.data;
        if (!result.error && isFaxScheduleSuccess(body)) {
            const account = (body as { data?: FaxAccountVo | null }).data;
            if (account && account.faxNumber) {
                message.success('Your fax number is ready.');
            } else {
                message.success('Fax number provisioned successfully!');
            }
            finishProvision(account);
        } else {
            message.error((body as { message?: string })?.message || result?.error || 'Provision failed');
        }
        setLoading(false);
    };

    return (
        <Spin spinning={loading} wrapperClassName={s.spinWrap}>
            <div className={s.root}>
                <div className={s.topSection}>
                    <div className={ps.backLink} onClick={onBack}>
                        <span className={ps.backIcon}>‹</span>
                        Back
                    </div>
                    <div className={ps.header}>
                        <div className={ps.headerTitle}>Get a new U.S.-based fax number</div>
                    </div>
                    <div className={s.searchRow}>
                        <div className={s.fieldCol}>
                            <div className={s.fieldLabel}>State</div>
                            <StateSelect
                                value={state}
                                size="large"
                                onChange={(val) => setState(val as string)}
                                placeholder="Select a state"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={s.fieldCol}>
                            <div className={s.fieldLabel}>Areacode (Optional)</div>
                            <InputNumber
                                value={areaCode}
                                size="large"
                                maxLength={3}
                                onChange={(val) => setAreaCode(val as string)}
                                placeholder="Please enter at least 3 characters"
                                controls={false}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <Button
                        type="primary"
                        className={ps.actionBtn}
                        disabled={!state}
                        onClick={handleSearch}
                        style={{ marginTop: 16 }}
                    >
                        Search
                    </Button>
                </div>

                {searched && numbers.length > 0 && (
                    <>
                        <div className={s.scrollArea}>
                            <div className={s.resultList}>
                                {numbers.map((item) => (
                                    <div
                                        key={item.number}
                                        className={`${s.numberCard} ${selectedNumber === item.number ? s.selected : ''}`}
                                        onClick={() => setSelectedNumber(item.number)}
                                    >
                                        <div className={s.numberText}>{formatPhoneNumber(item.number)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={s.confirmFooter}>
                            <Button
                                type="primary"
                                className={ps.actionBtn}
                                disabled={!selectedNumber}
                                onClick={handleConfirm}
                                style={{ marginTop: 0, marginBottom: 0 }}
                            >
                                Confirm number
                            </Button>
                        </div>
                    </>
                )}

                {searched && numbers.length === 0 && !loading && (
                    <div className={s.noResult}>No numbers found. Try a different state or region.</div>
                )}
            </div>
        </Spin>
    );
};

export default NewNumberFlow;
